<script setup>
/**
 * タイムチャート作成：対象期間の条件入力（DEV-14983）
 *
 * - 期間の設定（対象期間の選択）は独立したパネルにする
 * - 期間全体の CM・予告分数、スクリーンごとの清掃間隔（初期値は劇場・スクリーン設定の清掃間隔）
 * - 作品ごとの優先度（1〜10）・目標回数（複数行）・配給要望・劇場要望（自由記述）
 * 前回保存した値はバックエンドが初期値として返す。保存は「期間の設定を保存」で一括 PUT。
 * 対象期間の選択（期間ピッカー・保存済みの期間・この期間を削除）は親から period スロットで受け取り「期間の設定」パネルに置く。
 */
import { computed, ref, watch } from "vue";
import { usePlanState, toTargetRow } from "../use-plan";
import {
  COUNT_UNIT_OPTIONS,
  hasScreenCapability,
  periodLabel,
  screenCapabilityLabel,
  versionLabel,
} from "../utils";

// 分数入力の上限
const MINUTE_MAX = 120;
// 回数入力の上限
const COUNT_MAX = 999;
// 自由記述の文字数上限
const TEXT_MAX = 2000;

const state = usePlanState();
const {
  canEdit, days, adMinute, trailerMinute, screens, movies, targets, inputDirty, inputSaving, textsDirty,
} = state;

// 作品ごとの設定の展開状態（デフォルトは全作品展開）
const expandedMovieIds = ref([]);
// 展開済みとして扱った作品 ID（ユーザーが折りたたんだ作品を再読込時に勝手に開き直さないため）
const knownMovieIds = new Set();

/**
 * 作品一覧が変わったら、新しく現れた作品だけを展開状態に追加する
 */
watch(
  () => movies.value.map((m) => m.schedMovieId),
  (ids) => {
    const added = ids.filter((id) => !knownMovieIds.has(id));
    added.forEach((id) => knownMovieIds.add(id));
    if (added.length) {
      expandedMovieIds.value = [...expandedMovieIds.value, ...added];
    }
  },
  { immediate: true },
);

const periodFirst = computed(() => days.value[0]);
const periodLast = computed(() => days.value[days.value.length - 1]);

/**
 * 対象期間かつ作品の上映可能期間（runFrom〜runTo）以外の日付は選べないようにする
 */
const disabledDateOf = (movie) => (date) => {
  const y = date.getFullYear();
  const str = `${y}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const from = movie.runFrom && movie.runFrom > periodFirst.value ? movie.runFrom : periodFirst.value;
  const to = movie.runTo && movie.runTo < periodLast.value ? movie.runTo : periodLast.value;
  return str < from || str > to;
};

/**
 * 作品の目標回数行
 */
const targetsOf = (schedMovieId) => targets.value.filter((t) => t.schedMovieId === schedMovieId);

const addTarget = (movie) => {
  targets.value.push(toTargetRow({
    schedMovieId: movie.schedMovieId,
    dateFrom: movie.runFrom && movie.runFrom > periodFirst.value ? movie.runFrom : periodFirst.value,
    dateTo: movie.runTo && movie.runTo < periodLast.value ? movie.runTo : periodLast.value,
    countUnit: "TOTAL",
  }));
};

const removeTarget = (row) => {
  const index = targets.value.indexOf(row);
  if (index !== -1) targets.value.splice(index, 1);
};

/**
 * 期間ピッカー（daterange）と dateFrom / dateTo の相互変換
 */
const rangeOf = (row) => (row.dateFrom && row.dateTo ? [row.dateFrom, row.dateTo] : null);
const setRange = (row, value) => {
  row.dateFrom = value?.[0] || null;
  row.dateTo = value?.[1] || null;
};

/**
 * 折りたたみ時の見出しに出す概要
 */
const movieSummary = (movie) => {
  const list = targetsOf(movie.schedMovieId);
  const parts = [list.length ? `目標 ${list.length}件` : "目標なし"];
  if (movie.distributorText) parts.push("配給要望あり");
  if (movie.cinemaRequestText) parts.push("劇場要望あり");
  return parts.join(" / ");
};

const targetLabel = (movie, t) => {
  const ver = t.versionId == null
    ? "全バージョン"
    : versionLabel((movie.versions || []).find((v) => v.versionId === t.versionId));
  const unit = t.countUnit === "PER_DAY" ? "回/日" : "回";
  return `${ver} / ${periodLabel(t.dateFrom, t.dateTo)} / ${t.count ?? "-"}${unit}`;
};
</script>

<template>
  <div class="week-input">
    <!-- 期間の設定：対象期間の選択（親の index.vue から渡す） -->
    <section v-if="$slots.period" class="panel">
      <div class="panel-head">
        <h3>期間の設定</h3>
        <span class="panel-note">カレンダーの印が付いた日は排班済みです。選ぶと重なる日の上映回は上書きされます</span>
      </div>
      <div class="period-row">
        <span class="field__label">対象期間</span>
        <slot name="period" />
      </div>
    </section>

    <!-- 上映時間の設定（CM・予告・清掃間隔） -->
    <section class="panel">
      <div class="panel-head">
        <h3>上映時間の設定</h3>
        <span class="panel-note">初回はCM・予告は前回の期間、清掃間隔は「劇場・スクリーン設定」の値を初期表示します</span>
      </div>
      <div class="week-grid">
        <div class="field">
          <label class="field__label" for="plan-ad-minute">CM（分）</label>
          <el-input-number
            id="plan-ad-minute"
            v-model="adMinute"
            :min="0"
            :max="MINUTE_MAX"
            :step="1"
            step-strictly
            controls-position="right"
            :disabled="!canEdit"
          />
        </div>
        <div class="field">
          <label class="field__label" for="plan-trailer-minute">予告（分）</label>
          <el-input-number
            id="plan-trailer-minute"
            v-model="trailerMinute"
            :min="0"
            :max="MINUTE_MAX"
            :step="1"
            step-strictly
            controls-position="right"
            :disabled="!canEdit"
          />
        </div>
        <p class="hint week-grid__hint">上映開始＝CM開始。終了＝開始＋CM＋予告＋本編。各上映回で個別に変更できます</p>
      </div>

      <el-table :data="screens" row-key="screenId" class="screen-table">
        <el-table-column label="スクリーン" min-width="140">
          <template #default="{ row }">
            <span class="strong">{{ row.name }}</span>
            <span class="muted">（{{ row.maxSeats }}席）</span>
          </template>
        </el-table-column>
        <el-table-column label="上映可能時間帯" width="130">
          <template #default="{ row }">{{ row.openTime }}–{{ row.closeTime }}</template>
        </el-table-column>
        <el-table-column label="上映可能バージョン" min-width="220">
          <template #default="{ row }">
            <span v-if="hasScreenCapability(row)" class="muted">{{ screenCapabilityLabel(row) }}</span>
            <span v-else class="warn-text">未設定</span>
          </template>
        </el-table-column>
        <el-table-column label="清掃間隔（分）" width="160">
          <template #default="{ row }">
            <el-input-number
              v-model="row.cleaningMinute"
              :min="0"
              :max="MINUTE_MAX"
              :step="1"
              step-strictly
              size="small"
              controls-position="right"
              :disabled="!canEdit"
              :aria-label="`${row.name}の清掃間隔（分）`"
              class="minute-input"
            />
          </template>
        </el-table-column>
      </el-table>
    </section>

    <!-- 作品ごとの設定 -->
    <section class="panel">
      <div class="panel-head">
        <h3>作品ごとの設定<span class="panel-count">{{ movies.length }}</span></h3>
        <span class="panel-note">要望を変更すると「ルール確認」で自動的に再解析します</span>
      </div>

      <el-empty v-if="!movies.length" description="上映作品が登録されていません" :image-size="64" />

      <el-collapse v-else v-model="expandedMovieIds" class="movie-collapse">
        <el-collapse-item v-for="movie in movies" :key="movie.schedMovieId" :name="movie.schedMovieId">
          <template #title>
            <div class="movie-head">
              <el-image v-if="movie.posterUrl" :src="movie.posterUrl" fit="cover" lazy class="poster" />
              <span v-else class="poster poster--empty">No Image</span>
              <div class="movie-head__main">
                <div class="movie-head__title">{{ movie.title }}</div>
                <div class="movie-head__meta">
                  <span>{{ movie.durationMinute ? `${movie.durationMinute}分` : "上映時間 未設定" }}</span>
                  <span>優先度 {{ movie.priority ?? "未設定" }}</span>
                  <span v-if="movie.runFrom || movie.runTo">期間内の上映 {{ periodLabel(movie.runFrom || periodFirst, movie.runTo || periodLast) }}</span>
                  <span>{{ movieSummary(movie) }}</span>
                </div>
              </div>
            </div>
          </template>

          <div class="movie-body">
            <div class="field field--row">
              <span class="field__label">優先度</span>
              <el-rate
                class="priority-rate"
                v-model="movie.priority"
                :max="10"
                :disabled="!canEdit"
                show-score
                score-template="{value}"
                :aria-label="`${movie.title}の優先度`"
              />
            </div>

            <div class="field">
              <div class="field__label field__label--bar">
                <span>目標回数</span>
                <el-button
                  v-if="canEdit"
                  size="small"
                  type="primary"
                  plain
                  @click="addTarget(movie)"
                >
                  行を追加
                </el-button>
              </div>
              <p v-if="!targetsOf(movie.schedMovieId).length" class="hint">目標回数の指定はありません</p>
              <template v-else-if="canEdit">
                <div class="target-row target-row--head" aria-hidden="true">
                  <span class="target-ver">バージョン</span>
                  <span class="target-range">期間</span>
                  <span class="target-count">回数</span>
                  <span class="target-unit">単位</span>
                </div>
                <div v-for="(t, index) in targetsOf(movie.schedMovieId)" :key="t._key" class="target-row">
                  <!-- 未選択（null）＝全バージョン。el-option は null を値に取れないためクリアで表現する -->
                  <el-select
                    v-model="t.versionId"
                    clearable
                    placeholder="全バージョン"
                    class="target-ver"
                    :aria-label="`目標${index + 1}行目のバージョン`"
                    @clear="t.versionId = null"
                  >
                    <el-option
                      v-for="v in movie.versions"
                      :key="v.versionId"
                      :label="versionLabel(v)"
                      :value="v.versionId"
                    />
                  </el-select>
                  <el-date-picker
                    :model-value="rangeOf(t)"
                    type="daterange"
                    value-format="YYYY-MM-DD"
                    format="MM/DD"
                    range-separator="〜"
                    start-placeholder="開始"
                    end-placeholder="終了"
                    :default-value="periodFirst ? new Date(periodFirst) : undefined"
                    :disabled-date="disabledDateOf(movie)"
                    :clearable="false"
                    class="target-range"
                    @update:model-value="setRange(t, $event)"
                  />
                  <el-input-number
                    v-model="t.count"
                    :min="1"
                    :max="COUNT_MAX"
                    :step="1"
                    step-strictly
                    controls-position="right"
                    :aria-label="`目標${index + 1}行目の回数`"
                    class="target-count"
                  />
                  <el-select v-model="t.countUnit" :aria-label="`目標${index + 1}行目の単位`" class="target-unit">
                    <el-option v-for="u in COUNT_UNIT_OPTIONS" :key="u.value" :label="u.label" :value="u.value" />
                  </el-select>
                  <el-button text type="danger" :aria-label="`目標${index + 1}行目を削除`" @click="removeTarget(t)">
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
              </template>
              <ul v-else class="target-list">
                <li v-for="t in targetsOf(movie.schedMovieId)" :key="t._key">{{ targetLabel(movie, t) }}</li>
              </ul>
            </div>

            <div class="text-grid">
              <div class="field">
                <label class="field__label" :for="`dist-${movie.schedMovieId}`">配給要望</label>
                <el-input
                  :id="`dist-${movie.schedMovieId}`"
                  v-model="movie.distributorText"
                  type="textarea"
                  :autosize="{ minRows: 3, maxRows: 8 }"
                  :maxlength="TEXT_MAX"
                  show-word-limit
                  :disabled="!canEdit"
                  placeholder="例：初週は1日最低5回、うち1回はIMAXで上映"
                />
              </div>
              <div class="field">
                <label class="field__label" :for="`cinema-${movie.schedMovieId}`">劇場要望</label>
                <el-input
                  :id="`cinema-${movie.schedMovieId}`"
                  v-model="movie.cinemaRequestText"
                  type="textarea"
                  :autosize="{ minRows: 3, maxRows: 8 }"
                  :maxlength="TEXT_MAX"
                  show-word-limit
                  :disabled="!canEdit"
                  placeholder="例：週末の午前に吹替版を多めに、レイトショーは字幕版"
                />
              </div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </section>

    <div v-if="canEdit" class="input-actions">
      <span v-if="textsDirty" class="warn-text">要望が変更されています。「ルール確認」に進むと保存して自動的に再解析します</span>
      <span v-else-if="inputDirty" class="muted">未保存の変更があります</span>
      <el-button
        v-hasPermi="['dp:scheduleAi:plan:edit']"
        type="primary"
        :disabled="!inputDirty"
        :loading="inputSaving"
        @click="state.saveInput()"
      >
        期間の設定を保存
      </el-button>
    </div>
  </div>
</template>

<style lang="less" scoped>
// 対象期間の選択（期間ピッカー・保存済みの期間・この期間を削除）
.period-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 14px 18px;
}

.week-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 24px;
  padding: 14px 18px;

  &__hint {
    flex: 1;
    min-width: 240px;
    width: auto;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  &--row {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--sp-text-2);

    &--bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.screen-table {
  border-top: 1px solid var(--sp-border);
}

.minute-input {
  width: 120px;
}

.strong {
  font-weight: 600;
}

.movie-collapse {
  border-top: none;

  :deep(.el-collapse-item__header) {
    height: auto;
    min-height: 72px;
    padding: 0 18px;
    line-height: 1.4;
  }

  :deep(.el-collapse-item__content) {
    padding-bottom: 0;
  }
}

.movie-head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 8px 0;
  // Element Plus の折りたたみ見出しは <button> のため既定で中央揃えが継承される。
  // そのままだと題名がメタ情報の行より短い作品で題名が中央に寄り、前に空白があるように見えるので左揃えにする
  text-align: left;

  &__main {
    min-width: 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 14px;
    font-size: 12px;
    font-weight: 400;
    color: var(--sp-muted);
  }
}

.poster {
  flex: none;
  width: 40px;
  height: 56px;
  border-radius: 3px;
  background: color-mix(in srgb, var(--sp-muted) 10%, var(--sp-panel));

  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: var(--sp-muted);
  }
}

.movie-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 18px 18px 70px;
}

// 優先度の星：ウィンドウ幅で縮まないよう固定サイズにする（狭いときは見出しの下に折り返す）
.priority-rate {
  flex: none;
  height: auto;

  :deep(.el-rate__item) {
    flex: none;
  }

  // 1〜10 で数字の幅が変わっても星の位置がずれないよう幅を確保
  :deep(.el-rate__text) {
    min-width: 2em;
  }
}

// 目標回数の行：各入力欄は固定幅（ウィンドウ幅に合わせて伸び縮みさせない）。入りきらなければ折り返す
.target-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  & + & {
    margin-top: 8px;
  }

  &--head {
    font-size: 12px;
    color: var(--sp-muted);
  }

  > * {
    flex: none;
  }

  .target-ver {
    width: 220px;
  }

  .target-count {
    width: 120px;
  }

  .target-unit {
    width: 130px;
  }

  // 日付ピッカーのルートにはスコープ属性が付かないため :deep で指定する。
  // daterange は既定で固定幅（350px）なので狭め、MM/DD の開始・終了が見切れないよう詰める
  :deep(.target-range.el-date-editor),
  .target-range {
    flex: none;
    width: 240px;
    box-sizing: border-box;
  }

  :deep(.target-range .el-range-input) {
    flex: 1;
    min-width: 0;
  }

  :deep(.target-range .el-range-separator) {
    flex: none;
    padding: 0 4px;
  }
}

.target-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.8;
}

.text-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.input-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 0;
  background: var(--bg-body, var(--sp-panel));
}

@media (max-width: 960px) {
  .movie-body {
    padding-left: 18px;
  }

  .text-grid {
    grid-template-columns: 1fr;
  }

  // 折り返すと見出しと列がずれるため見出しは隠す
  .target-row--head {
    display: none;
  }
}
</style>
